// =============================================================================
// Admin Analytics — GraphQL enum ↔ UI vocabulary
// =============================================================================
// The UI keys its label/color maps on short lowercase vocab (`ReportStatus`,
// `QaCategory`, `TemplateType`); the API speaks generated GraphQL enums. These
// maps are the only place the two meet, so a backend rename surfaces as a type
// error here rather than as a silently unlabeled value.
// =============================================================================
import {
  AnalyticsQaCategory,
  AnalyticsQaFlagStatus,
  ReportAnalyticsStatus,
  ReportAnalyticsTemplateType,
} from "@api/graphql/types-and-hooks";
import { QaCategory, ReportStatus, TemplateType } from "@pages/AdminAnalytics/data/types";

export const TEMPLATE_TYPE_TO_API: Record<TemplateType, ReportAnalyticsTemplateType> = {
  IA: ReportAnalyticsTemplateType.Ia,
  RA: ReportAnalyticsTemplateType.Ra,
  DB: ReportAnalyticsTemplateType.Db,
};

export const TEMPLATE_TYPE_FROM_API: Record<ReportAnalyticsTemplateType, TemplateType> = {
  [ReportAnalyticsTemplateType.Ia]: "IA",
  [ReportAnalyticsTemplateType.Ra]: "RA",
  [ReportAnalyticsTemplateType.Db]: "DB",
};

export const STATUS_TO_API: Record<ReportStatus, ReportAnalyticsStatus> = {
  draft: ReportAnalyticsStatus.Draft,
  complete: ReportAnalyticsStatus.Complete,
  submitted: ReportAnalyticsStatus.Submitted,
  needs_revision: ReportAnalyticsStatus.NeedsRevision,
  approved: ReportAnalyticsStatus.Approved,
};

export const STATUS_FROM_API: Record<ReportAnalyticsStatus, ReportStatus> = {
  [ReportAnalyticsStatus.Draft]: "draft",
  [ReportAnalyticsStatus.Complete]: "complete",
  [ReportAnalyticsStatus.Submitted]: "submitted",
  [ReportAnalyticsStatus.NeedsRevision]: "needs_revision",
  [ReportAnalyticsStatus.Approved]: "approved",
};

export const CATEGORY_TO_API: Record<QaCategory, AnalyticsQaCategory> = {
  document_completeness: AnalyticsQaCategory.DocumentCompleteness,
  goals: AnalyticsQaCategory.Goals,
  insurance_standards: AnalyticsQaCategory.InsuranceStandards,
  custom: AnalyticsQaCategory.Custom,
  ccs_qa: AnalyticsQaCategory.CcsQa,
  clinical_content: AnalyticsQaCategory.ClinicalContent,
  other: AnalyticsQaCategory.Other,
};

export const CATEGORY_FROM_API: Record<AnalyticsQaCategory, QaCategory> = {
  [AnalyticsQaCategory.DocumentCompleteness]: "document_completeness",
  [AnalyticsQaCategory.Goals]: "goals",
  [AnalyticsQaCategory.InsuranceStandards]: "insurance_standards",
  [AnalyticsQaCategory.Custom]: "custom",
  [AnalyticsQaCategory.CcsQa]: "ccs_qa",
  [AnalyticsQaCategory.ClinicalContent]: "clinical_content",
  [AnalyticsQaCategory.Other]: "other",
};

export const QA_FLAG_STATUS_LABELS: Record<AnalyticsQaFlagStatus, string> = {
  [AnalyticsQaFlagStatus.Open]: "Open",
  [AnalyticsQaFlagStatus.Resolved]: "Resolved",
};

/** Keep only the strings that are members of `enumObj` (facet values arrive as plain strings). */
export const toEnumValues = <E extends string>(values: string[] | undefined, enumObj: Record<string, E>): E[] => {
  const members = new Set<string>(Object.values(enumObj));
  return (values ?? []).filter((v): v is E => members.has(v));
};
